type SeoConfig = {
  title: string;
  description: string;
  keywords: string;
  path: string;
  robots?: string;
};

const DEFAULT_SITE_URL = 'https://ctl.invested.no';
const DEFAULT_OG_IMAGE_PATH = '/assets/og-konsulent-preview.svg';

const DEFAULT_KEYWORDS =
  'medical strategist, emergency medicine advisor, board advisor healthcare, health tech consultant, medtech strategy';

const SEO_BY_PATH: Record<string, SeoConfig> = {
  '/': {
    title: 'Christer T. Larsen, MD | Medical Strategist & Board Advisor',
    description:
      'Board-level medical strategy, emergency medicine expertise, and health-tech integration for healthcare leaders and MedTech teams.',
    keywords: DEFAULT_KEYWORDS,
    path: '/',
  },
  '/contact': {
    title: 'Contact | Christer T. Larsen, MD',
    description:
      'Book a focused advisory conversation on medical strategy, emergency systems, and healthcare technology execution.',
    keywords: `${DEFAULT_KEYWORDS}, contact medical consultant, healthcare advisory inquiry`,
    path: '/contact',
  },
  '/privacy': {
    title: 'Privacy Policy | Christer T. Larsen, MD',
    description: 'Privacy policy for advisory inquiries and data handling on ctl.invested.no.',
    keywords: 'privacy policy, healthcare advisory privacy, data handling',
    path: '/privacy',
  },
  '/terms': {
    title: 'Terms of Use | Christer T. Larsen, MD',
    description: 'Terms of use for professional advisory information and engagement inquiries.',
    keywords: 'terms of use, advisory terms, healthcare consulting terms',
    path: '/terms',
  },
};

const normalizePath = (pathname: string): string => {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return clean;
};

const ensureLeadingSlash = (value: string): string => {
  if (!value) return '/';
  return value.startsWith('/') ? value : `/${value}`;
};

const resolveSiteUrl = (value?: string): string => {
  const raw = value?.trim();
  if (!raw) return DEFAULT_SITE_URL;
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');
  return `https://${raw}`.replace(/\/+$/, '');
};

const resolveAbsoluteUrl = (urlOrPath: string, siteUrl: string): string => {
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  return `${siteUrl}${ensureLeadingSlash(urlOrPath)}`;
};

const SITE_URL = resolveSiteUrl(import.meta.env.VITE_SITE_URL);
const OG_IMAGE_URL = resolveAbsoluteUrl(
  (import.meta.env.VITE_OG_IMAGE_URL ?? DEFAULT_OG_IMAGE_PATH).trim(),
  SITE_URL,
);

const upsertMetaByName = (name: string, content: string): void => {
  let tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertMetaByProperty = (property: string, content: string): void => {
  let tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertCanonical = (url: string): void => {
  let linkTag = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!linkTag) {
    linkTag = document.createElement('link');
    linkTag.setAttribute('rel', 'canonical');
    document.head.appendChild(linkTag);
  }
  linkTag.setAttribute('href', url);
};

export const getCanonicalUrl = (pathname: string): string => {
  const normalizedPath = normalizePath(pathname);
  const urlPath = normalizedPath === '/' ? '/' : ensureLeadingSlash(normalizedPath);
  return new URL(urlPath, `${SITE_URL}/`).toString();
};

export const applyRouteSeo = (pathname: string): void => {
  if (typeof document === 'undefined') return;

  const normalizedPath = normalizePath(pathname);
  const config = SEO_BY_PATH[normalizedPath] ?? SEO_BY_PATH['/'];
  const canonicalUrl = getCanonicalUrl(config.path);
  const robots = config.robots ?? 'index,follow,max-image-preview:large';

  document.title = config.title;

  upsertCanonical(canonicalUrl);
  upsertMetaByName('description', config.description);
  upsertMetaByName('keywords', config.keywords);
  upsertMetaByName('robots', robots);
  upsertMetaByName('apple-mobile-web-app-title', 'CTL Invested');

  upsertMetaByProperty('og:type', 'website');
  upsertMetaByProperty('og:url', canonicalUrl);
  upsertMetaByProperty('og:site_name', 'CTL Invested');
  upsertMetaByProperty('og:locale', 'en_US');
  upsertMetaByProperty('og:title', config.title);
  upsertMetaByProperty('og:description', config.description);
  upsertMetaByProperty('og:image', OG_IMAGE_URL);
  upsertMetaByProperty('og:image:width', '1200');
  upsertMetaByProperty('og:image:height', '630');
  upsertMetaByProperty('og:image:alt', 'CTL Invested advisory services preview');

  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:url', canonicalUrl);
  upsertMetaByName('twitter:title', config.title);
  upsertMetaByName('twitter:description', config.description);
  upsertMetaByName('twitter:image', OG_IMAGE_URL);
};
